/**
 * The product story's moving parts: the note journey and the figures that
 * draw themselves when reached. Custom elements, so the theme editor's
 * re-renders tear them down and set them up again on their own.
 */

/* Once through the notes when the section is seen on a desktop: long
   enough to read as a journey, short enough to be done before the eye
   moves on. */
const DEMO_MS = 4200;
/* Where scroll drives the rail: phones, and anything without hover. */
const SCROLL_DRIVEN = '(max-width: 900px), (hover: none)';
/* The strip's top at 85% of the viewport is the first note, at 50% the
   last. */
const SCROLL_START = 0.85;
const SCROLL_END = 0.5;

class ZivaNoteJourney extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('.ziva-pdp-notes__card')];
    this.strip = this.querySelector('.ziva-pdp-notes__strip');
    this.range = this.querySelector('.ziva-pdp-notes__range');
    this.stops = [...this.querySelectorAll('.ziva-pdp-notes__stops li')];
    this.stages = (this.dataset.stages || '').split(',').filter(Boolean);
    this.last = Math.max(this.cards.length - 1, 1);
    this.position = 0;
    this.manual = false;
    this.demoed = false;
    this.abort = new AbortController();
    const {signal} = this.abort;

    this.cards.forEach((card, index) => {
      card.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.choose(index);
      }, {signal});
      card.querySelector('.ziva-pdp-notes__pick')?.addEventListener('click', () => this.choose(index), {signal});
    });

    if (this.range) {
      this.range.addEventListener('input', () => {
        this.takeOver();
        this.setPosition(Number(this.range.value));
      }, {signal});
      this.range.addEventListener('pointerdown', () => this.takeOver(), {signal});
      this.range.addEventListener('pointerup', () => this.setPosition(Math.round(Number(this.range.value))), {signal});
      this.range.addEventListener('keydown', (event) => {
        const active = this.active();
        const moves = {
          ArrowRight: active + 1, ArrowUp: active + 1, ArrowLeft: active - 1, ArrowDown: active - 1,
          PageUp: active + 1, PageDown: active - 1, Home: 0, End: this.last,
        };
        if (!(event.key in moves)) return;
        event.preventDefault();
        this.choose(Math.min(this.last, Math.max(0, moves[event.key])));
      }, {signal});
    }

    this.scrollQuery = window.matchMedia(SCROLL_DRIVEN);
    this.onScroll = () => {
      if (!this.tick) this.tick = requestAnimationFrame(() => this.follow());
    };
    this.setup = () => this.configure();
    this.scrollQuery.addEventListener('change', this.setup, {signal});
    this.render();
    if (this.cards.length > 1) this.configure();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.teardown();
  }

  active() {
    return Math.min(this.cards.length - 1, Math.max(0, Math.round(this.position)));
  }

  takeOver() {
    cancelAnimationFrame(this.frame);
    this.manual = true;
  }

  choose(index) {
    this.takeOver();
    this.setPosition(index);
  }

  setPosition(position) {
    this.position = position;
    this.render();
  }

  render() {
    const active = this.active();
    this.cards.forEach((card, index) => {
      card.dataset.active = String(index === active);
      card.querySelector('.ziva-pdp-notes__pick')?.setAttribute('aria-pressed', String(index === active));
    });
    this.stops.forEach((stop, index) => {
      stop.dataset.active = String(index === active);
      stop.dataset.reached = String(index <= active);
    });
    const color = this.cards[active]?.dataset.noteColor;
    this.style.setProperty('--note-glow', color || 'var(--pdp-accent)');
    if (this.range) {
      this.range.value = String(this.position);
      this.range.style.setProperty('--fill', `${(this.position / this.last) * 100}%`);
      const name = this.cards[active]?.querySelector('.ziva-pdp-notes__pick')?.textContent.trim() ?? '';
      this.range.setAttribute('aria-valuetext', this.stages.length ? `${this.stages[active]}: ${name}` : name);
    }
  }

  follow() {
    this.tick = 0;
    if (this.manual || !this.strip) return;
    const view = window.innerHeight;
    const rect = this.strip.getBoundingClientRect();
    const travel = rect.height + view * (SCROLL_START - SCROLL_END);
    const progress = (view * SCROLL_START - rect.top) / travel;
    this.setPosition(Math.min(this.last, Math.max(0, Math.floor(progress * this.cards.length))));
  }

  teardown() {
    this.observer?.disconnect();
    this.observer = undefined;
    cancelAnimationFrame(this.frame);
    cancelAnimationFrame(this.tick);
    this.tick = 0;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
  }

  configure() {
    this.teardown();
    if (this.scrollQuery.matches) {
      window.addEventListener('scroll', this.onScroll, {passive: true});
      window.addEventListener('resize', this.onScroll);
      this.follow();
    } else if (!this.demoed && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.observer = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting || this.demoed || this.manual) return;
        this.observer?.disconnect();
        this.demoed = true;
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / DEMO_MS);
          const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
          this.setPosition(eased * this.last);
          if (t < 1) this.frame = requestAnimationFrame(step);
        };
        this.frame = requestAnimationFrame(step);
      }, {threshold: 0.55});
      this.observer.observe(this);
    }
  }
}

if (!customElements.get('ziva-note-journey')) customElements.define('ziva-note-journey', ZivaNoteJourney);

/* ------------------------------------------------------------- reveal
   <ziva-reveal hidden> marks its parent with data-reveal: 'idle' while it
   waits below the fold, 'live' once the reader reaches it. Absent (no
   script) is the finished state in the stylesheet, so a figure is never
   simply missing. */
class ZivaReveal extends HTMLElement {
  connectedCallback() {
    if (document.body.dataset.animations === 'off') {
      this.parentElement?.setAttribute('data-reveal', 'live');
      return;
    }
    const target = this.parentElement;
    if (!target) return;
    if (typeof IntersectionObserver === 'undefined') {
      target.dataset.reveal = 'live';
      return;
    }
    target.dataset.reveal = 'idle';
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        target.dataset.reveal = 'live';
        this.observer.disconnect();
      }
    }, {rootMargin: '-12% 0px'});
    this.observer.observe(target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('ziva-reveal')) customElements.define('ziva-reveal', ZivaReveal);
